import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { PokemonCard, FilterState } from '../types/pokemon';
import { useDebounce } from './useDebounce';
import { useAuth } from './useAuth';
import { useFilterOptions } from './useFilterOptions';
import { useSetFilterOptions } from './useSetFilterOptions';
import { useCollection } from './useCollection';

const ITEMS_PER_PAGE = 50;
// Cache des résultats pour éviter des requêtes redondantes
const queryCache = new Map<string, {data: PokemonCard[], count: number, timestamp: number}>();
// Durée de validité du cache en ms (5 minutes)
const CACHE_TTL = 5 * 60 * 1000;

export function useCards(filters: FilterState) {
  const [cards, setCards] = useState<PokemonCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { user } = useAuth();
  const abortController = useRef<AbortController | null>(null);
  const [packSet, setPackSet] = useState<string | null>(null);
  
  const filterOptions = useFilterOptions();
  const setFilterOptions = useSetFilterOptions(filters.set);
  const debouncedSearch = useDebounce(filters.search, 300);
  const { collection } = useCollection();

  // Fonction pour générer une clé de cache unique basée sur les filtres actuels
  const generateCacheKey = (pageNum: number) => {
    return JSON.stringify({
      page: pageNum,
      set: filters.set,
      pack: filters.pack,
      packSet: packSet,
      rarity: filters.rarity,
      type: filters.type,
      energy: filters.energy,
      search: debouncedSearch,
      showMissing: filters.showMissing,
      collectionIds: filters.showMissing ? Object.keys(collection).filter(id => collection[parseInt(id)].quantity > 0) : []
    });
  };

  // Charger le set du pack sélectionné si nécessaire
  useEffect(() => {
    const loadPackSet = async () => {
      if (!filters.pack || filters.pack === 'all') {
        setPackSet(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('packs')
          .select('set')
          .eq('name', filters.pack)
          .single();

        if (error) throw error;
        setPackSet(data?.set || null);
      } catch (err) {
        console.error('Error loading pack set:', err);
        setPackSet(null);
      }
    };

    loadPackSet();
  }, [filters.pack]);

  const fetchCards = async (pageToFetch: number, shouldAppend = false) => {
    if (!user) return;

    // Annuler la requête précédente si elle existe
    if (abortController.current) {
      abortController.current.abort();
    }
    abortController.current = new AbortController();

    // Vérifier le cache d'abord
    const cacheKey = generateCacheKey(pageToFetch);
    const now = Date.now();
    const cachedResult = queryCache.get(cacheKey);

    if (cachedResult && (now - cachedResult.timestamp < CACHE_TTL)) {
      if (shouldAppend) {
        setCards(prevCards => {
          // Éviter les doublons
          const existingIds = new Set(prevCards.map(card => card.id));
          const newCards = cachedResult.data.filter(card => !existingIds.has(card.id));
          return [...prevCards, ...newCards];
        });
      } else {
        setCards(cachedResult.data);
      }
      
      setTotalCount(cachedResult.count);
      setHasMore(cachedResult.data.length === ITEMS_PER_PAGE && (!cachedResult.count || (pageToFetch * ITEMS_PER_PAGE + cachedResult.data.length) < cachedResult.count));
      
      if (shouldAppend) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
      
      return;
    }

    try {
      if (shouldAppend) {
        setIsLoadingMore(true);
      } else {
        setLoading(true);
      }

      // Sélectionner uniquement les champs nécessaires au lieu de *
      let query = supabase
        .from('cards')
        .select(
          'id, card_id, card_en, card_fr, pack, rarity, set, image, type, energy, first_third_cards, fourth_card, fifth_card',
          { count: 'exact' }
        );

      // Appliquer les filtres
      if (filters.set) {
        // Si un set est spécifié dans les filtres, on l'utilise
        query = query.eq('set', filters.set);
      } else if (packSet) {
        // Sinon, si on a un pack sélectionné, on utilise son set
        query = query.eq('set', packSet);
      }

      if (filters.pack) {
        if (filters.pack === 'all') {
          // Si 'all' est sélectionné, on ne filtre pas par pack
          query = query;
        } else if (filters.pack === 'Mission') {
          // Pour le pack Mission, on ne prend que les cartes de ce pack
          query = query.eq('pack', filters.pack);
        } else {
          // Pour tout autre pack, on inclut les cartes du pack et les cartes 'All'
          query = query.in('pack', [filters.pack, 'All']);
        }
      }

      if (filters.rarity) {
        query = query.eq('rarity', filters.rarity);
      }
      if (filters.type) {
        query = query.eq('type', filters.type);
      }
      if (filters.energy) {
        query = query.eq('energy', filters.energy);
      }
      if (debouncedSearch) {
        query = query.or(`card_fr.ilike.%${debouncedSearch}%,card_en.ilike.%${debouncedSearch}%`);
      }
      if (filters.showMissing) {
        // Récupérer uniquement les IDs des cartes qui ont une quantité > 0
        const collectedCardIds = Object.entries(collection)
          .filter(([_, card]) => card.quantity > 0)
          .map(([id, _]) => parseInt(id));
        
        if (collectedCardIds.length > 0) {
          query = query.not('id', 'in', `(${collectedCardIds.join(',')})`);
        }
      }

      // Pagination
      const start = pageToFetch * ITEMS_PER_PAGE;
      const end = start + ITEMS_PER_PAGE - 1;
      query = query
        .range(start, end)
        .order('card_id', { ascending: true, nullsFirst: false });

      const { data, count, error: queryError } = await query;

      if (queryError) throw queryError;

      if (data) {
        // Stocker dans le cache
        queryCache.set(cacheKey, {
          data: data as PokemonCard[],
          count: count || 0,
          timestamp: now
        });

        setTotalCount(count || 0);
        
        if (shouldAppend) {
          setCards(prevCards => {
            // Créer un Set des IDs existants pour éviter les doublons
            const existingIds = new Set(prevCards.map(card => card.id));
            // Ne garder que les nouvelles cartes
            const newCards = data.filter(card => !existingIds.has(card.id));
            return [...prevCards, ...newCards];
          });
        } else {
          setCards(data);
        }
        setHasMore(data.length === ITEMS_PER_PAGE && (!count || (start + data.length) < count));
      }

    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      console.error('Error loading cards:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
      setHasMore(false);
    } finally {
      if (shouldAppend) {
        setIsLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  // Effet pour recharger les cartes quand les filtres changent
  useEffect(() => {
    setPage(0);
    fetchCards(0, false);

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [
    debouncedSearch,
    filters.pack,
    filters.rarity,
    filters.set,
    filters.showMissing,
    filters.type,
    filters.energy,
    user?.id,
    JSON.stringify(collection),
    packSet
  ]);

  // Purger les entrées du cache obsolètes
  useEffect(() => {
    const now = Date.now();
    for (const [key, value] of queryCache.entries()) {
      if (now - value.timestamp > CACHE_TTL) {
        queryCache.delete(key);
      }
    }
  }, [cards]);

  const loadMore = async () => {
    if (!isLoadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      await fetchCards(nextPage, true);
    }
  };

  // Utiliser les filtres du set si un set est sélectionné
  const currentFilterOptions = useMemo(() => {
    if (filters.set) {
      return {
        ...filterOptions,
        packs: setFilterOptions.packs,
        rarities: setFilterOptions.rarities,
        types: setFilterOptions.types,
        energies: setFilterOptions.energies
      };
    }
    return filterOptions;
  }, [filters.set, filterOptions, setFilterOptions]);

  return {
    cards,
    loading,
    error,
    hasMore,
    isLoadingMore,
    loadMore,
    filterOptions: currentFilterOptions,
    totalCount,
    refresh: () => {
      // Vider le cache lors d'un rafraîchissement explicite
      const cacheKey = generateCacheKey(0);
      queryCache.delete(cacheKey);
      
      setLoading(true);
      setPage(0);
      fetchCards(0, false);
    },
  };
}